import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Save, Send, X, AlertCircle, Loader, QrCode,
  Camera, Upload, Building2, Users, FileText,
  AlertTriangle, MapPin, Image as ImageIcon,
  Clock, Tag, ChevronRight, Info, CheckCircle2,
  AlertOctagon
} from 'lucide-react';
import { TICKET_STATUS } from '../utils/permissions';
import AssetSelector from './AssetSelector';
import QRScanner from './QRScanner';
import BackButton from '../../../components/BackButton';

const TicketForm = ({ user, onSubmit, initialData }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAssetSelector, setShowAssetSelector] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    problemType: '',
    description: '',
    location: '',
    subLocation: '',
    department: user?.department || '',
    priority: 'medium',
    assetId: '',
    assetDetails: null,
    images: [],
    ...initialData
  });

  // Predefined data
  const locations = [
    { id: 'main', name: 'Main Building', subLocations: ['Kitchen', 'Dining Area', 'Restrooms', 'Storage'] },
    { id: 'warehouse', name: 'Warehouse', subLocations: ['Loading Dock', 'Storage Area', 'Office'] },
    { id: 'outlet1', name: 'Outlet #1', subLocations: ['Front Counter', 'Kitchen', 'Dining'] },
  ];

  const departments = [
    'Operations',
    'Kitchen',
    'Maintenance',
    'Facilities',
    'IT',
    'Administration'
  ];

  const problemTypes = [
    { category: 'Facilities', types: ['HVAC', 'Plumbing', 'Electrical', 'Structural'] },
    { category: 'Equipment', types: ['Kitchen Equipment', 'Office Equipment', 'Safety Equipment'] },
    { category: 'IT', types: ['Hardware', 'Software', 'Network', 'POS System'] },
    { category: 'Safety', types: ['Fire Safety', 'Security System', 'Emergency Equipment'] },
  ];

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title?.trim()) newErrors.title = 'Title is required';
    if (!formData.problemType) newErrors.problemType = 'Problem type is required';
    if (!formData.description?.trim()) newErrors.description = 'Description is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.department) newErrors.department = 'Department is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file)
    }));

    setPreviewImages(prev => [...prev, ...newImages]);
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, ...files]
    }));
  };

  const removeImage = (imageId) => {
    setPreviewImages(prev => prev.filter(img => img.id !== imageId));
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => 
        previewImages[index].id !== imageId
      )
    }));
  };

  const handleQRScan = (data) => {
    // Handle QR scan result
    setShowQRScanner(false);
    if (data) {
      // Process scanned asset data
      setFormData(prev => ({
        ...prev,
        assetId: data.id,
        assetDetails: data
      }));
    }
  };

  const handleSubmit = async (asDraft = false) => {
    if (!asDraft && !validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const ticketData = {
        ...formData,
        status: asDraft ? TICKET_STATUS.DRAFT : TICKET_STATUS.SUBMITTED,
        submittedBy: user.id,
        submittedDate: new Date().toISOString(),
        department: user.department
      };

      // Handle image uploads first
      if (formData.images.length > 0) {
        const uploadedImages = await Promise.all(
          formData.images.map(async (image) => {
            const formData = new FormData();
            formData.append('file', image);
            // Replace with your actual image upload API call
            // const response = await api.uploadImage(formData);
            // return response.url;
            return URL.createObjectURL(image); // Mock response for now
          })
        );
        ticketData.imageUrls = uploadedImages;
      }

      const result = await onSubmit(ticketData);
      navigate('/maintenance');
    } catch (error) {
      setErrors({ submit: error.message || 'Failed to create ticket' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header Section with subtle glow */}
        <div className="flex items-center justify-between mb-6 relative">
          <div className="flex items-center gap-4">
            <BackButton className="hover:shadow-md transition-shadow" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 drop-shadow-sm">
                {initialData ? 'Edit Maintenance Ticket' : 'New Maintenance Ticket'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Submit a maintenance request for issues that need attention
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Status</span>
            <span className="px-3 py-1 rounded-full text-sm font-medium bg-yellow-100/80 text-yellow-700 backdrop-blur-sm shadow-sm ring-1 ring-yellow-200/50">
              Draft
            </span>
          </div>
        </div>

        {/* Main Form with enhanced shadows */}
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="col-span-2 space-y-6">
            {/* Basic Information Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-200/80 overflow-hidden
                          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)] 
                          transition-all duration-300 ease-in-out">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
                  <Info className="w-5 h-5 text-blue-500" />
                  Basic Information
                </h2>

                <div className="space-y-6">
                  {/* Title Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ticket Title
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className={`block w-full rounded-lg border ${
                        errors.title ? 'border-red-300' : 'border-gray-300'
                      } px-4 py-3 focus:border-blue-500 focus:ring-blue-500`}
                      placeholder="Enter a clear title for the issue"
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>

                  {/* Problem Type & Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Problem Type
                      </label>
                      <div className="relative">
                        <select
                          value={formData.problemType}
                          onChange={(e) => setFormData({ ...formData, problemType: e.target.value })}
                          className="block w-full rounded-lg border border-gray-300 px-4 py-3 appearance-none focus:border-blue-500 focus:ring-blue-500"
                        >
                          <option value="">Select type</option>
                          {problemTypes.map(category => (
                            <optgroup key={category.category} label={category.category}>
                              {category.types.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 transform rotate-90" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Priority Level
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {['low', 'medium', 'high'].map((priority) => (
                          <button
                            key={priority}
                            type="button"
                            onClick={() => setFormData({ ...formData, priority })}
                            className={`px-4 py-3 rounded-lg text-sm font-medium ${
                              formData.priority === priority
                                ? priority === 'high'
                                  ? 'bg-red-100 text-red-700 border-2 border-red-200'
                                  : priority === 'medium'
                                  ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-200'
                                  : 'bg-blue-100 text-blue-700 border-2 border-blue-200'
                                : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Location & Asset Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-200/80 overflow-hidden
                          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]
                          transition-all duration-300 ease-in-out">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
                  <MapPin className="w-5 h-5 text-emerald-500" />
                  Location & Asset
                </h2>

                <div className="space-y-6">
                  {/* Location Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <select
                        value={formData.location}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          location: e.target.value,
                          subLocation: ''
                        })}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">Select location</option>
                        {locations.map(loc => (
                          <option key={loc.id} value={loc.id}>{loc.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sub Location
                      </label>
                      <select
                        value={formData.subLocation}
                        onChange={(e) => setFormData({ ...formData, subLocation: e.target.value })}
                        disabled={!formData.location}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        <option value="">Select sub-location</option>
                        {formData.location && locations
                          .find(loc => loc.id === formData.location)
                          ?.subLocations.map(subLoc => (
                            <option key={subLoc} value={subLoc}>{subLoc}</option>
                          ))
                        }
                      </select>
                    </div>
                  </div>

                  {/* Asset Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Related Asset
                    </label>
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={() => setShowQRScanner(true)}
                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <QrCode className="w-5 h-5 text-gray-500" />
                        Scan QR Code
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAssetSelector(true)}
                        className="flex items-center gap-2 px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Building2 className="w-5 h-5 text-gray-500" />
                        Select Asset
                      </button>
                    </div>
                    {formData.assetDetails && (
                      <div className="mt-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-blue-900">{formData.assetDetails.name}</h4>
                            <p className="text-sm text-blue-700 mt-1">ID: {formData.assetDetails.id}</p>
                          </div>
                          <button
                            onClick={() => setFormData({ ...formData, assetId: '', assetDetails: null })}
                            className="p-1 hover:bg-blue-100 rounded"
                          >
                            <X className="w-4 h-4 text-blue-600" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-200/80 overflow-hidden
                          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]
                          transition-all duration-300 ease-in-out">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Issue Details
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={6}
                    className={`block w-full rounded-lg border ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    } px-4 py-3 focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="Provide detailed information about the issue..."
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Department Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-200/80 overflow-hidden
                          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]
                          transition-all duration-300 ease-in-out">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
                  <Users className="w-5 h-5 text-indigo-500" />
                  Department
                </h2>

                <select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select department</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Images Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-200/80 overflow-hidden
                          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]
                          transition-all duration-300 ease-in-out">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-6">
                  <ImageIcon className="w-5 h-5 text-purple-500" />
                  Images
                </h2>

                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  
                  <div className="space-y-4">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-32 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Upload className="w-8 h-8 text-gray-400" />
                      <span className="mt-2 text-sm text-gray-500">Add Photos</span>
                    </button>

                    {previewImages.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {previewImages.map((image) => (
                          <div key={image.id} className="relative group">
                            <img
                              src={image.preview}
                              alt="Preview"
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(image.id)}
                              className="absolute -top-2 -right-2 p-1.5 bg-red-100 rounded-full text-red-600 hover:bg-red-200 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons Card */}
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-gray-200/80 overflow-hidden
                          shadow-[0_4px_20px_-4px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_-5px_rgba(0,0,0,0.1)]
                          transition-all duration-300 ease-in-out">
              <div className="p-6 space-y-4">
                <button
                  type="button"
                  onClick={() => handleSubmit(false)}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 border border-transparent rounded-lg text-sm font-medium 
                           text-white bg-blue-600 hover:bg-blue-700 
                           shadow-[0_2px_8px_-2px_rgba(59,130,246,0.5)]
                           hover:shadow-[0_4px_12px_-2px_rgba(59,130,246,0.6)]
                           focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                           disabled:opacity-50 disabled:cursor-not-allowed
                           transition-all duration-300"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Submitting...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <Send className="w-4 h-4" />
                      <span>Submit Ticket</span>
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium 
                           text-gray-700 bg-white hover:bg-gray-50
                           shadow-[0_2px_6px_-2px_rgba(0,0,0,0.05)]
                           hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.1)]
                           focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                           transition-all duration-300"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Save className="w-4 h-4" />
                    <span>Save as Draft</span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/maintenance')}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium 
                           text-gray-700 bg-white hover:bg-gray-50
                           shadow-[0_2px_6px_-2px_rgba(0,0,0,0.05)]
                           hover:shadow-[0_4px_12px_-2px_rgba(0,0,0,0.1)]
                           focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                           transition-all duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Modals */}
      {showAssetSelector && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm">
          <AssetSelector
            onSelect={(asset) => {
              setFormData({
                ...formData,
                assetId: asset.id,
                assetDetails: asset
              });
              setShowAssetSelector(false);
            }}
            onClose={() => setShowAssetSelector(false)}
          />
        </div>
      )}

      {showQRScanner && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm">
          <QRScanner
            onScan={handleQRScan}
            onClose={() => setShowQRScanner(false)}
          />
        </div>
      )}
    </div>
  );
};

export default TicketForm; 